import { computed, Directive, input, model, ModelSignal } from '@angular/core';
import { FormUiControl, FormValueControl, ValidationError } from '@angular/forms/signals';

/**
 * The `FormUiControl` half of a low-level signal-forms field: everything the
 * `[formField]` directive pushes *into* a control, plus the bootstrap validation
 * state derived from it. Carries no value member at all, because the two signal-forms
 * control contracts disagree about it — `FormValueControl` requires `value` and
 * forbids `checked`, `FormCheckboxControl` the other way round.
 *
 * Value-based fields should extend {@link SignalFieldBaseDirective} (or
 * {@link SignalStringFieldBaseDirective}); a checkbox extends this directly and adds
 * its own `checked` model.
 *
 * Nothing here knows about the store or any domain.
 */
@Directive()
export abstract class SignalFieldUiDirective implements FormUiControl {
    // --- `FormUiControl` inputs, all bound automatically by `[formField]` -----

    public readonly errors = input<readonly ValidationError.WithOptionalFieldTree[]>([]);
    public readonly disabled = input(false);
    public readonly readonly = input(false);
    public readonly required = input(false);

    /**
     * A model rather than an input: the `[formField]` directive cannot observe a
     * blur on a custom component's inner control, so the field reports the first
     * interaction itself.
     */
    public readonly touched = model(false);

    /** consumers are expected to pass a field-specific label; this is only a fallback */
    public readonly ariaLabel = input('Input');

    /**
     * Whether the field prints its own validation messages below the input.
     *
     * Set to `false` when the field is one half of a composite control — the composite is bound to
     * the form field and owns the message, so each half printing it too would say the same thing
     * twice. The invalid outline stays either way, since that marks *which* half needs attention.
     */
    public readonly showErrorMessages = input(true);

    /**
     * Whether the field accepts input at all — the one name for "the two states that
     * mean the value is not the user's to change".
     *
     * The pair is kept as two inputs because the framework binds two and the
     * native controls tell them apart: a readonly input can still be focused and
     * its text copied, a disabled one cannot. Whether a *control* may be operated,
     * though, is one question, and every field that cannot express readonly
     * natively — the checkbox, the radio, and the button-based ones — answers it
     * with this rather than deriving it again.
     */
    public readonly editable = computed(() => !this.disabled() && !this.readonly());

    /**
     * Bootstrap's validation classes, applied exactly as
     * `FormValidationMessageDirective` applies them to the reactive-forms inputs:
     * the theme turns `is-valid` into the green outline plus checkmark and
     * `is-invalid` into the red outline plus exclamation mark. Driven by validity
     * alone, not by `touched`, so an empty required field reads as "input expected".
     *
     * A field that is not editable carries no validation state at all, matching how
     * `FormValidationMessageDirective` drops both classes for a disabled control
     * (the reactive-forms widgets disable their control when readonly).
     */
    public readonly isInvalid = computed(() => this.editable() && this.errors().length > 0);
    public readonly isValid = computed(() => this.editable() && this.errors().length === 0);

    /** Whether the validation messages are to be printed — invalid, and not suppressed by a composite. */
    public readonly hasErrorMessages = computed(() => this.isInvalid() && this.showErrorMessages());
}

/**
 * {@link SignalFieldUiDirective} for the fields that edit a value — everything except
 * the checkbox. `value` stays abstract because both its type and its empty
 * representation differ per field: `''` for the string fields, `null` for the
 * numeric ones.
 */
@Directive()
export abstract class SignalFieldBaseDirective<T> extends SignalFieldUiDirective implements FormValueControl<T> {
    /** the edited value; kept in sync with the bound field by the `[formField]` directive */
    public abstract readonly value: ModelSignal<T>;
}

/**
 * {@link SignalFieldBaseDirective} for the string-valued fields — date, time, text
 * and multiline text. An unset value is the empty string, which is what the native
 * controls report when cleared.
 */
@Directive()
export abstract class SignalStringFieldBaseDirective extends SignalFieldBaseDirective<string> {
    public readonly value = model<string>('');

    public onInput(event: Event): void {
        this.value.set((<HTMLInputElement | HTMLTextAreaElement>event.target).value);
    }
}
